/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	popupContent: {
		paddingTop: token('space.100'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.150'),
		width: '330px',
		maxHeight: '400px',
		overflowY: 'auto',
	},
	popupHeader: {
		marginBottom: token('space.025'),
	},
	popupHeaderTitleRow: {
		paddingLeft: token('space.100'),
	},
	headerDivider: {
		// Expand the divider to the full popup width by cancelling out the
		// popupContent horizontal padding.
		marginTop: token('space.050'),
		marginRight: token('space.negative.150'),
		marginBottom: token('space.100'),
		marginLeft: token('space.negative.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	popupHeaderLabel: {
		paddingLeft: token('space.100'),
	},
	sourcesList: {
		paddingLeft: token('space.0'),
		listStyle: 'none',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
	},
});

export type SourcesPopupPresentationProps = {
	/** Number displayed in the popup heading. */
	sourceCount: number;
	/** Caller-owned source rows. */
	children: React.ReactNode;
	/** Closes the controlled popup. */
	onClose: () => void;
};

export const SourcesPopupPresentation = ({
	sourceCount,
	children,
	onClose,
}: SourcesPopupPresentationProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const sourcesLabel = formatMessage(messages.sourcesTitle, { numSources: sourceCount });

	return (
		<Box xcss={styles.popupContent} role="dialog" aria-label={sourcesLabel} aria-modal="false">
			<Box xcss={styles.popupHeader}>
				<Inline spread="space-between" alignBlock="center" xcss={styles.popupHeaderTitleRow}>
					<Inline alignBlock="center" space="space.100">
						<TeamworkGraphIcon label="" />
						<Heading as="h2" size="xsmall">
							{formatMessage(messages.sourcesSidePanelTeamworkGraphTitle)}
						</Heading>
					</Inline>
					<IconButton
						appearance="subtle"
						icon={(props) => <CrossIcon {...props} size="small" />}
						label={formatMessage(messages.closeSourcesPopup)}
						onClick={onClose}
						autoFocus
					/>
				</Inline>
				<Box xcss={styles.headerDivider} />
				<Box xcss={styles.popupHeaderLabel}>
					<Text color="color.text.subtle" weight="bold" size="small">
						{sourcesLabel}
					</Text>
				</Box>
			</Box>
			<Stack as="ul" xcss={styles.sourcesList} space="space.050">
				{children}
			</Stack>
		</Box>
	);
};
